import {
  DrilldownDataSchemaContextType,
  useDrilldownSchema,
} from 'features/drilldown/rolodex/DrilldownDataSchema'
import {
  convertKeyToLabel,
  DrilldownField,
  DrilldownRolodexType,
} from 'features/drilldown/rolodex/ontology/ontologyTypes'
import _ from 'lodash'
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'

import {
  TwoStepDropdown,
  TwoStepFieldSelectOption,
} from './components/TwoStepSelect'

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: left;
  gap: 1rem;
`

type GroupByFieldSelectProps = {
  label?: ReactNode
  fields?: DrilldownDataSchemaContextType['fields']
  selectedField: DrilldownField
  setSelectedField: Dispatch<SetStateAction<DrilldownField>>
  placeholder?: string
  width?: string
}

export const DrilldownTypeAndFieldSelect: FC<GroupByFieldSelectProps> = ({
  label,
  selectedField,
  setSelectedField,
  fields: propFields,
  placeholder,
  width = '240px',
}) => {
  const { fields: hookFields, entryTypeNames } = useDrilldownSchema()
  const fields = useMemo(
    () => propFields ?? hookFields,
    [hookFields, propFields]
  )

  const primaryOptions = useMemo<ReadonlyArray<TwoStepFieldSelectOption>>(
    () =>
      _.uniq(Object.values(fields).map((field) => field.entryType))
        .map((entryType) => ({
          value: entryType,
          label: entryTypeNames[entryType] ?? convertKeyToLabel(entryType),
        }))
        .toSorted(),
    [fields, entryTypeNames]
  )
  const [primarySelection, setPrimarySelection] =
    useState<DrilldownRolodexType>(primaryOptions[0]?.value)

  const secondaryOptions = useMemo<
    ReadonlyMap<DrilldownRolodexType, ReadonlyArray<TwoStepFieldSelectOption>>
  >(
    () =>
      _.reduce(
        Object.entries(fields),
        (acc, [field, { entryType, name }]) => {
          const option = { label: name, value: field }
          const fieldList = acc.get(entryType) ?? []
          const newAcc = new Map(acc)
          newAcc.set(entryType, [...fieldList, option])
          return newAcc
        },
        new Map()
      ),
    [fields]
  )

  useEffect(() => {
    setPrimarySelection((prev) => fields[selectedField]?.entryType ?? prev)
  }, [fields, primaryOptions, secondaryOptions, selectedField])

  return (
    <Container>
      {label ?? <></>}
      <TwoStepDropdown
        primaryOptions={primaryOptions}
        secondaryOptions={secondaryOptions}
        primarySelection={primarySelection}
        setPrimarySelection={setPrimarySelection}
        secondarySelection={selectedField}
        setSecondarySelection={setSelectedField}
        placeholder={placeholder}
        width={width}
      />
    </Container>
  )
}
